// Custom Digital Workforce agent logos — MomentumAMPAPI /api/AgentLogo.
// Images live in a public blob container, so logoUrl works as a plain <img src>.

export interface AgentLogoItem {
  agentId: string;
  logoUrl: string;
}

const BASE = `${process.env.REACT_APP_Auth_Url || ''}/api/AgentLogo`;

function authHeader(): Record<string, string> {
  return { Authorization: `Bearer ${localStorage.getItem('authToken') || ''}` };
}

/** Every response carries a user-facing `message` (e.g. "Only the agent's owner can change its logo."). */
async function readResult<T>(res: Response, fallback: string): Promise<T> {
  const json = await res.json().catch(() => null);
  if (!res.ok || json?.success === false) throw new Error(json?.message || fallback);
  return json as T;
}

export async function listAgentLogos(): Promise<AgentLogoItem[]> {
  const res = await fetch(`${BASE}/list`, { headers: authHeader() });
  const json = await readResult<{ data?: AgentLogoItem[] }>(res, "Couldn't load agent logos.");
  return json.data ?? [];
}

export async function uploadAgentLogo(agentId: string, image: Blob): Promise<string> {
  const form = new FormData();
  form.append('file', image, 'logo.png');
  const res = await fetch(`${BASE}/${encodeURIComponent(agentId)}`, {
    method: 'POST',
    headers: authHeader(), // no Content-Type — the browser sets the multipart boundary
    body: form,
  });
  const json = await readResult<{ logoUrl?: string }>(res, "Couldn't save the logo. Please try again.");
  if (!json.logoUrl) throw new Error("Couldn't save the logo. Please try again.");
  return json.logoUrl;
}

export async function deleteAgentLogo(agentId: string): Promise<void> {
  const res = await fetch(`${BASE}/${encodeURIComponent(agentId)}`, {
    method: 'DELETE',
    headers: authHeader(),
  });
  await readResult(res, "Couldn't remove the logo. Please try again.");
}

/** After importing a shared agent: give the new copy the shared agent's logo. */
export async function copyAgentLogoFromShare(token: string, agentId: string): Promise<string | null> {
  const res = await fetch(`${BASE}/copy-from-share`, {
    method: 'POST',
    headers: { ...authHeader(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, agentId }),
  });
  const json = await readResult<{ logoUrl?: string | null }>(res, "Couldn't copy the agent's logo.");
  return json.logoUrl ?? null;
}
